import React, { useEffect, useState } from 'react'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { trans } from 'sharedKernel/i18n'
import { ROUTES } from 'sharedKernel/constants'
import { getHubRoute, formatDateForDisplay } from 'sharedKernel/handlers'
import { DEFAULT_OPPORTUNITY_IMAGE } from '../../constants'
import type { IResolverOpportunity } from '../../contracts'
import {
  displayOrDash,
  formatVerticalDisplay,
  getDescriptionPreview,
  getResolverOpportunityCoverImage,
  getStartupInitial,
  isOpportunityClosed,
} from '../../behavior'
import * as S from './style'

interface OpportunityCardItemProps {
  opportunity: IResolverOpportunity
}

export const OpportunityCardItem = ({
  opportunity,
}: OpportunityCardItemProps): JSX.Element => {
  const navigate = useNavigate()
  const [imageFailed, setImageFailed] = useState(false)
  const isClosed = isOpportunityClosed(opportunity)

  useEffect(() => {
    setImageFailed(false)
  }, [opportunity.id])

  const coverSrc = imageFailed
    ? DEFAULT_OPPORTUNITY_IMAGE
    : getResolverOpportunityCoverImage(opportunity)

  const deadlineLabel = isClosed
    ? trans('scouting.opportunity.card.closed')
    : `${trans('scouting.opportunity.card.openUntil')} ${displayOrDash(
        opportunity.interestDeadline ? formatDateForDisplay(opportunity.interestDeadline) || null : null
      )}`

  const descriptionPreview = getDescriptionPreview(opportunity.description)
  const interestedStartups = opportunity.interestedStartups?.slice(0, 3) ?? []
  const statusLabel = isClosed
    ? trans('scouting.opportunity.card.closed')
    : trans('scouting.opportunity.card.open')

  const handleLearnMore = (): void => {
    navigate(
      getHubRoute(ROUTES.HUB.STARTUP.GET_OPPORTUNITY).replace(':opportunityId', opportunity.id)
    )
  }

  return (
    <S.OpportunityCard
      $isClosed={isClosed}
      aria-label={`${displayOrDash(opportunity.title)} — ${statusLabel}`}
    >
      <S.CardContent>
        <S.CardImageWrapper>
          <S.CardImage
            src={coverSrc}
            alt={displayOrDash(opportunity.title)}
            onError={() => setImageFailed(true)}
          />
        </S.CardImageWrapper>

        <S.CardBody>
          <S.CardUpperContent>
            <S.CardHeaderRow>
              <S.CardTitle>{displayOrDash(opportunity.title)}</S.CardTitle>
              <S.StatusBadge $isClosed={isClosed}>{deadlineLabel}</S.StatusBadge>
            </S.CardHeaderRow>

            <S.CompanyName>{displayOrDash(opportunity.requesterName)}</S.CompanyName>

            <S.DescriptionRow>
              <S.CardDescription title={descriptionPreview}>{descriptionPreview}</S.CardDescription>

              {interestedStartups.length > 0 && (
                <S.AvatarGroup>
                  {interestedStartups.map((startup, index) => (
                    <S.AvatarItem key={`${startup.name ?? 'startup'}-${index}`} $index={index}>
                      {startup.logo ? (
                        <img src={startup.logo} alt={startup.name ?? ''} />
                      ) : (
                        getStartupInitial(startup.name)
                      )}
                    </S.AvatarItem>
                  ))}
                </S.AvatarGroup>
              )}
            </S.DescriptionRow>
          </S.CardUpperContent>

          <S.CardDivider />

          <S.CardFooter>
            <S.FooterMetaGroup>
              <S.FooterMetaItem>
                <S.FooterMetaLabel>{trans('scouting.opportunity.card.impactVertical')}</S.FooterMetaLabel>
                <S.FooterMetaValue>{formatVerticalDisplay(opportunity)}</S.FooterMetaValue>
              </S.FooterMetaItem>
              <S.FooterMetaItem>
                <S.FooterMetaLabel>{trans('scouting.opportunity.card.maxValue')}</S.FooterMetaLabel>
                <S.FooterMetaValue>{displayOrDash(opportunity.maxValue)}</S.FooterMetaValue>
              </S.FooterMetaItem>
            </S.FooterMetaGroup>

            <S.LearnMoreButton type="button" onClick={handleLearnMore}>
              {trans('scouting.opportunity.card.learnMore')}
            </S.LearnMoreButton>
          </S.CardFooter>
        </S.CardBody>
      </S.CardContent>
    </S.OpportunityCard>
  )
}
